// src/store/orders-store.ts
import { create } from "zustand";
import { persist, PersistStorage } from "zustand/middleware";
import { v4 as uuidv4 } from "uuid";
import { Order, Category } from "../interfaces/domain/menu";

const CART_TTL = 2 * 60 * 60 * 1000;
const TIMESTAMP_KEY = "orders-storage-timestamp";

interface State {
  orders: Order[];
  canUseCart: boolean;

  addItem: (item: Omit<Order, "cartItemId">) => void;
  incrementItem: (cartItemId: string) => void;
  decrementItem: (cartItemId: string) => void;
  removeItem: (cartItemId: string) => void;
  updateItem: (cartItemId: string, updatedOrder: Order) => void;
  setCanUseCart: (value: boolean) => void;
  revalidatePrices: (categories: Category[]) => boolean;
  clear: () => void;
}

const storage: PersistStorage<{ orders: Order[] }> = {
  getItem: (name) => {
    const str = localStorage.getItem(name);
    return str ? JSON.parse(str) : null;
  },
  setItem: (name, value) => {
    localStorage.setItem(name, JSON.stringify(value));
    localStorage.setItem(TIMESTAMP_KEY, Date.now().toString());
  },
  removeItem: (name) => localStorage.removeItem(name),
};

export const useOrdersStore = create<State>()(
  persist(
    (set, get) => ({
      orders: [],
      canUseCart: false,

      addItem: (item) =>
        set({ orders: [...get().orders, { ...item, cartItemId: uuidv4() }] }),

      incrementItem: (cartItemId) =>
        set({
          orders: get().orders.map((o) =>
            o.cartItemId === cartItemId ? { ...o, quantity: o.quantity + 1 } : o
          ),
        }),

      decrementItem: (cartItemId) => {
        const item = get().orders.find((o) => o.cartItemId === cartItemId);
        if (!item) return;
        if (item.quantity > 1) {
          set({
            orders: get().orders.map((o) =>
              o.cartItemId === cartItemId ? { ...o, quantity: o.quantity - 1 } : o
            ),
          });
        } else {
          get().removeItem(cartItemId);
        }
      },

      removeItem: (cartItemId) =>
        set({ orders: get().orders.filter((o) => o.cartItemId !== cartItemId) }),

      updateItem: (cartItemId, updatedOrder) =>
        set({
          orders: get().orders.map((o) =>
            o.cartItemId === cartItemId ? { ...updatedOrder, cartItemId } : o
          ),
        }),

      setCanUseCart: (value) => set({ canUseCart: value }),

      revalidatePrices: (categories) => {
        const allProducts = categories.flatMap((c) => c.products);
        let anyChanged = false;

        const newOrders = get().orders.map((order) => {
          const product = allProducts.find((p) => p.id === order.productId);
          if (!product || product.price === null) return order;

          let extras = 0;
          const freshResolvedOptions: Array<{
            groupName: string;
            items: Array<{ name: string; qty: number; price: number }>;
          }> = [];

          if (order.optionSelection) {
            for (const [groupId, selections] of Object.entries(order.optionSelection)) {
              const group = product.options.find((g) => g.id === groupId);
              if (!group) continue;
              const items: Array<{ name: string; qty: number; price: number }> = [];
              for (const [itemId, qty] of Object.entries(selections)) {
                const item = group.items.find((i) => i.id === itemId);
                if (!item) continue;
                if (group.adds_price) extras += item.price * qty;
                items.push({ name: item.name, qty, price: item.price });
              }
              if (items.length > 0) freshResolvedOptions.push({ groupName: group.name, items });
            }
          }

          const basePrice = order.urlOverride?.price ?? product.price;
          const newPrice = basePrice + extras;
          // order.price === 0 es rehidratación desde localStorage, no un cambio real
          if (order.price > 0 && newPrice !== order.price) anyChanged = true;

          return {
            ...order,
            price: newPrice,
            resolvedOptions: freshResolvedOptions.length > 0
              ? freshResolvedOptions
              : order.resolvedOptions,
          };
        });

        set({ orders: newOrders });
        return anyChanged;
      },

      clear: () => set({ orders: [] }),
    }),
    {
      name: "orders-storage",
      storage,
      partialize: (state) => ({
        orders: state.orders.map((order) => ({ ...order, price: 0 })),
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const last = localStorage.getItem(TIMESTAMP_KEY);
        if (last && Date.now() - parseInt(last) > CART_TTL) {
          state.clear();
          localStorage.removeItem(TIMESTAMP_KEY);
        }
      },
    }
  )
);
